import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import ToolbarFilterDropdown from '../components/ToolbarFilterDropdown';
import './Customers.css';
import './TimesheetList.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function TimesheetList() {
  const [timesheets, setTimesheets] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const pageSizeDdRef = useRef(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(null);
  const { canCreate } = useAuth();

  const load = () => {
    setLoading(true);
    return api
      .get('/timesheets')
      .then(setTimesheets)
      .catch(() => setTimesheets([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!pageSizeOpen) return undefined;
    const close = () => setPageSizeOpen(false);
    const onDoc = e => {
      if (pageSizeDdRef.current && !pageSizeDdRef.current.contains(e.target)) close();
    };
    const onKey = e => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [pageSizeOpen]);

  const downloadTemplate = () => {
    window.location.href = `${window.location.origin}/api/timesheets/csv-template`;
  };

  const statusFilterOptions = useMemo(() => {
    const uniq = new Set(timesheets.map(t => t.status).filter(Boolean));
    const sorted = [...uniq].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return [{ value: '', label: 'All Statuses' }, ...sorted.map(s => ({ value: s, label: s }))];
  }, [timesheets]);

  const monthFilterOptions = useMemo(() => {
    const uniq = new Set(timesheets.map(t => t.billing_month).filter(Boolean));
    const sorted = [...uniq].sort((a, b) => b.localeCompare(a, undefined));
    return [{ value: '', label: 'All months' }, ...sorted.map(m => ({ value: m, label: m }))];
  }, [timesheets]);

  const filtered = useMemo(
    () =>
      timesheets.filter(t => {
        if (statusFilter && t.status !== statusFilter) return false;
        if (monthFilter && t.billing_month !== monthFilter) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return [
          t.timesheet_id,
          t.employee_id,
          t.employee_name,
          t.customer_name,
          t.customer_id,
          t.rfp_num,
          t.submitted_by_name,
        ]
          .some(v => (v || '').toString().toLowerCase().includes(q));
      }),
    [timesheets, search, statusFilter, monthFilter]
  );

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, monthFilter, pageSize]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(filtered.length / pageSize));
    setPage(p => Math.min(p, tp));
  }, [filtered.length, pageSize]);

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const offset = (safePage - 1) * pageSize;
  const pageRows = filtered.slice(offset, offset + pageSize);
  const rangeStart = totalFiltered === 0 ? 0 : offset + 1;
  const rangeEnd = totalFiltered === 0 ? 0 : Math.min(offset + pageSize, totalFiltered);

  const hasFilters = !!(search || statusFilter || monthFilter);
  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setMonthFilter('');
  };

  const fmtHours = v => {
    if (v == null || v === '') return '—';
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : String(v);
  };

  return (
    <>
      <div className="page-header">
        <h1>Timesheets</h1>
      </div>
      <div className="page-content timesheet-list-page">
        <div className="customers-top">
          <div className="customers-stats" aria-label="Timesheet counts">
            <div className="customers-stat">
              <div className="customers-stat-label">In list</div>
              <div className="customers-stat-value">{loading ? '—' : timesheets.length.toLocaleString()}</div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Match filters</div>
              <div className="customers-stat-value">{loading ? '—' : filtered.length.toLocaleString()}</div>
            </div>
          </div>
          <div className="customers-top-right">
            {hasFilters && (
              <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>
                Clear filters
              </button>
            )}
            <button type="button" className="btn btn-secondary btn-sm" onClick={downloadTemplate}>
              Download CSV Template
            </button>
            {canCreate('timesheets') && (
              <Link to="/timesheets/new" className="btn btn-primary btn-sm customers-new-btn">
                + Update Timesheet
              </Link>
            )}
          </div>
        </div>

        {timesheets.length > 0 && filtered.length < timesheets.length && (
          <div className="customers-filter-hint" role="status">
            <span>
              Filters narrow the table to <strong>{filtered.length}</strong> of <strong>{timesheets.length}</strong>{' '}
              timesheet{timesheets.length === 1 ? '' : 's'} in your current list.
            </span>
            <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}

        <div className="customers-toolbar">
          <div className="customers-toolbar-left">
            <div className="customers-search">
              <span className="customers-search-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path d="M16.3 16.3 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by employee, customer, RFP, timesheet ID…"
                aria-label="Search timesheets"
              />
            </div>
            <div className="customers-filters">
              <ToolbarFilterDropdown
                id="ts-filter-status"
                labelText="Status"
                ariaLabel="Filter by status"
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="All Statuses"
                options={statusFilterOptions}
                menuKey="ts-status"
                openMenu={filterMenuOpen}
                setOpenMenu={setFilterMenuOpen}
              />
              <ToolbarFilterDropdown
                id="ts-filter-month"
                labelText="Billing month"
                ariaLabel="Filter by billing month"
                value={monthFilter}
                onChange={setMonthFilter}
                placeholder="All months"
                options={monthFilterOptions}
                menuKey="ts-month"
                openMenu={filterMenuOpen}
                setOpenMenu={setFilterMenuOpen}
              />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="timesheet-list-table">
            <table>
              <thead>
                <tr>
                  <th>Timesheet ID</th>
                  <th>Employee ID</th>
                  <th>Employee</th>
                  <th>Customer</th>
                  <th>RFP #</th>
                  <th>Billing Month</th>
                  <th>Period</th>
                  <th className="timesheet-list-num">Total Hrs</th>
                  <th>Status</th>
                  <th>Submitted By</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(t => (
                  <tr key={t.timesheet_id}>
                    <td>
                      <Link to={`/timesheets/${t.timesheet_id}`} className="link">
                        {t.timesheet_id}
                      </Link>
                    </td>
                    <td title={t.employee_id}>{t.employee_id}</td>
                    <td title={t.employee_name}>{t.employee_name}</td>
                    <td title={t.customer_name || t.customer_id}>{t.customer_name || t.customer_id}</td>
                    <td>{t.rfp_num || '—'}</td>
                    <td>{t.billing_month || '—'}</td>
                    <td title={`${t.period_from} → ${t.period_to}`}>
                      {t.period_from} → {t.period_to}
                    </td>
                    <td className="timesheet-list-num">{fmtHours(t.total_hours)}</td>
                    <td>
                      <span className={`badge badge-${(t.status || '').toLowerCase()}`}>{t.status}</span>
                    </td>
                    <td title={t.submitted_by_name || ''}>{t.submitted_by_name || '—'}</td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={10} className="timesheet-list-empty">
                      {loading
                        ? 'Loading timesheets…'
                        : timesheets.length === 0
                          ? 'No timesheets found.'
                          : 'No timesheets match the filters.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!loading && totalFiltered > 0 && (
            <div className="customers-pagination" role="navigation" aria-label="Table pagination">
              <div className="customers-pagination-summary">
                <span className="customers-pagination-summary-label">Showing</span>
                <span className="customers-pagination-summary-range">
                  <strong className="tabular-nums">{rangeStart}</strong>
                  <span className="customers-pagination-summary-dash">–</span>
                  <strong className="tabular-nums">{rangeEnd}</strong>
                  <span className="customers-pagination-summary-of">of</span>
                  <strong className="tabular-nums">{totalFiltered.toLocaleString()}</strong>
                  <span className="customers-pagination-summary-unit">rows</span>
                </span>
              </div>
              <div className="customers-pagination-controls">
                <div className="customers-pagination-page-size" ref={pageSizeDdRef}>
                  <span className="customers-pagination-label" id="ts-page-size-label">
                    Rows per page
                  </span>
                  <div className="customers-page-size-dd-inner">
                    <button
                      type="button"
                      className={`customers-page-size-trigger ${pageSizeOpen ? 'customers-page-size-trigger-open' : ''}`}
                      aria-haspopup="listbox"
                      aria-expanded={pageSizeOpen}
                      aria-label={`Rows per page, current value ${pageSize}`}
                      onClick={() => setPageSizeOpen(o => !o)}
                    >
                      <span>{pageSize}</span>
                      <span className="customers-page-size-chevron" aria-hidden>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </span>
                    </button>
                    {pageSizeOpen && (
                      <ul className="customers-page-size-menu" role="listbox" aria-labelledby="ts-page-size-label">
                        {PAGE_SIZE_OPTIONS.map(n => (
                          <li
                            key={n}
                            role="option"
                            aria-selected={pageSize === n}
                            className={
                              pageSize === n
                                ? 'customers-page-size-option customers-page-size-option-active'
                                : 'customers-page-size-option'
                            }
                            onMouseDown={e => {
                              e.preventDefault();
                              setPageSize(n);
                              setPageSizeOpen(false);
                            }}
                          >
                            {n}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="customers-pagination-pager" role="group" aria-label="Page navigation">
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage <= 1}
                    onClick={() => setPage(1)}
                    aria-label="First page"
                    title="First page"
                  >
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 19l-7-7 7-7" />
                        <path d="M18 19l-7-7 7-7" />
                      </svg>
                    </span>
                    <span>First</span>
                  </button>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    aria-label="Previous page"
                    title="Previous page"
                  >
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </span>
                    <span>Prev</span>
                  </button>
                  <div className="customers-pagination-page-chip">
                    <span className="customers-pagination-page-label">Page</span>
                    <span className="customers-pagination-page-current tabular-nums">{safePage}</span>
                    <span className="customers-pagination-page-sep">/</span>
                    <span className="customers-pagination-page-total tabular-nums">{totalPages}</span>
                  </div>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    aria-label="Next page"
                    title="Next page"
                  >
                    <span>Next</span>
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(totalPages)}
                    aria-label="Last page"
                    title="Last page"
                  >
                    <span>Last</span>
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M13 5l7 7-7 7" />
                        <path d="M6 5l7 7-7 7" />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
